import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { useLiveDuration, useLiveEdgeProgress } from '@dtvgo/player-utils-react';

import { useKeyMoments } from './useKeyMoments';
import { DEFAULT_VISIBLE_CATEGORIES } from './types';

import { getKeyMoments } from '../../services/keyMomentsService';
import { IS_KEY_MOMENTS_ACTIVE } from '../../utils/env';
import { mergeKeyMomentsSameMinute } from './mergeKeyMomentsSameMinute';

const MAX_REASONABLE_MATCH_MINUTE = 130;

/** Programme start (unix s): accepts Date, ISO string or numeric `asset.live.startTime`. */
function parseStreamStartSeconds(liveStart) {
  if (liveStart === null || liveStart === undefined) return 0;
  if (liveStart instanceof Date) {
    const t = liveStart.getTime() / 1000;
    return Number.isFinite(t) ? t : 0;
  }
  const parsed = new Date(liveStart);
  const t = parsed.getTime() / 1000;
  return Number.isFinite(t) && t > 0 ? t : 0;
}

/**
 * `@dtvgo/slider` drops gap positions whose normalized coord is ≤0.001 or ≥0.999
 * (`generateGapMaskStyle`), which hides cuts near the programme edges.
 */
function nudgeGapOffsetForSliderMask(offsetSeconds, durationSeconds) {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return offsetSeconds;
  const ratio = offsetSeconds / durationSeconds;
  if (ratio <= 0.001) return durationSeconds * 0.002;
  if (ratio >= 0.999) return durationSeconds * 0.998;
  return offsetSeconds;
}

function getFallbackHalf(minutes) {
  if (minutes <= 45) return 1;
  if (minutes <= 90) return 2;
  if (minutes <= 105) return 3;
  if (minutes <= 120) return 4;
  return 5;
}

function getFallbackMatchTime(timestampSeconds, streamStartSeconds) {
  // eslint-disable-next-line unicorn/no-useless-undefined
  if (!timestampSeconds || !streamStartSeconds) return undefined;
  const elapsedSeconds = Math.max(0, Math.round(timestampSeconds - streamStartSeconds));

  return {
    minutes: Math.floor(elapsedSeconds / 60),
    seconds: elapsedSeconds % 60,
  };
}

function normalizeDisplayTime(item, streamStartTimestamp) {
  if (item.minutes <= MAX_REASONABLE_MATCH_MINUTE) return item;
  const fallback = getFallbackMatchTime(item.timestamp, streamStartTimestamp);
  if (!fallback) return item;
  return {
    ...item,
    half: getFallbackHalf(fallback.minutes),
    minutes: fallback.minutes,
    seconds: fallback.seconds,
  };
}

/**
 * Mouse-driven version for dtvweb. Mirrors the dtvott-webostv-app hook but
 * removes spatial-navigation concerns (no setFocus / arrow keys).
 *
 * Carousel recenters on `selectedId` (immersive ‹ › moves selection without seek); click seeks.
 * When `keyMomentsCarouselOpen` closes, selection resets to the latest moment — no playback seek.
 * Use only with Player UI **V2** — pass `playerUiV2={false}` on V1 so the API is not polled.
 */
export default function useKeyMomentsPlayer({
  asset,
  isLive,
  handleLiveSeek,
  seekable,
  authorizerVrioId,
  // When false (Player UI V1), key moments are fully disabled — no polling, no seek from KM.
  playerUiV2 = false,
  /** V2 carousel overlay; closing resets carousel selection to newest moment without seeking. */
  keyMomentsCarouselOpen = false,
}) {
  const liveDuration = useLiveDuration();
  const liveEdgeProgress = useLiveEdgeProgress();

  const programId = authorizerVrioId || '';

  const [isPollingBlocked, setIsPollingBlocked] = useState(false);

  useEffect(() => {
    setIsPollingBlocked(false);
  }, [authorizerVrioId]);

  const isFeatureEnabled = IS_KEY_MOMENTS_ACTIVE
    && playerUiV2
    && isLive
    && !!programId
    && !isPollingBlocked;

  const streamStartTimestamp = useMemo(
    () => parseStreamStartSeconds(asset?.live?.startTime),
    [asset?.live?.startTime],
  );

  const { items: rawItems, errorStatusCode } = useKeyMoments(
    getKeyMoments,
    isFeatureEnabled ? programId : '',
    isFeatureEnabled,
  );

  useEffect(() => {
    if (errorStatusCode !== undefined
        && errorStatusCode >= 400
        && errorStatusCode <= 499) {
      setIsPollingBlocked(true);
    }
  }, [errorStatusCode]);

  const items = useMemo(() => {
    const mapped = rawItems
      .filter((item) => DEFAULT_VISIBLE_CATEGORIES.includes(item.category))
      .map((item) => normalizeDisplayTime(item, streamStartTimestamp));

    let list = mapped;

    if (isFeatureEnabled && isLive && streamStartTimestamp > 0) {
      // Drop moments past the playable “now” so markers/cards don’t sit beyond the live edge.
      const slackSec = 3;
      const wallCapSec = Math.max(0, Date.now() / 1000 - streamStartTimestamp);
      const edgeCapSec = Number.isFinite(liveEdgeProgress) && liveEdgeProgress > 1
        ? liveEdgeProgress
        : wallCapSec;
      const horizonSec = Math.min(wallCapSec, edgeCapSec) + slackSec;

      list = mapped.filter((item) => (
        item.timestamp - streamStartTimestamp <= horizonSec
      ));
    }

    return mergeKeyMomentsSameMinute(list);
  }, [rawItems, streamStartTimestamp, isFeatureEnabled, isLive, liveEdgeProgress]);

  const enabled = isFeatureEnabled && items.length > 0;

  /**
   * Seconds on programme axis (`0..liveDuration`) for `@dtvgo/progress-bar` `gaps`.
   * Every list moment (including unavailable) gets an offset; values are nudged so
   * `@dtvgo/slider` does not drop edge gaps (mask filter trims ≤0.1% / ≥99.9%).
   */
  const gaps = useMemo(() => {
    if (!enabled || streamStartTimestamp <= 0) {
      // eslint-disable-next-line unicorn/no-useless-undefined
      return undefined;
    }
    const d = liveDuration;
    return items.map((item) => nudgeGapOffsetForSliderMask(
      item.timestamp - streamStartTimestamp,
      d,
    ));
  }, [enabled, items, liveDuration, streamStartTimestamp]);

  /**
   * Moments before oldest seekable second (`seekable.start`) aren’t rewindable (DVR window).
   * The carousel still lists them as context; seek/click handlers ignore those ids.
   */
  const unavailableIds = useMemo(() => {
    const start = seekable?.start;
    // eslint-disable-next-line unicorn/no-useless-undefined
    if (!streamStartTimestamp || !start || start <= 0) return undefined;
    const ids = new Set();
    for (const item of items) {
      if (item.timestamp - streamStartTimestamp < start) {
        const toFlag = item.mergedIncidenceIds?.length > 0
          ? item.mergedIncidenceIds
          : [item.incidenceId];
        for (const id of toFlag) {
          ids.add(id);
        }
      }
    }
    // eslint-disable-next-line unicorn/no-useless-undefined
    return ids.size > 0 ? ids : undefined;
  }, [items, seekable?.start, streamStartTimestamp]);

  const [centerIndex, setCenterIndex] = useState(0);
  const hasInitializedRef = useRef(false);
  const prevCarouselOpenRef = useRef(keyMomentsCarouselOpen);

  /**
   * Carousel open/close: snap selection to newest moment (last in list); no seek.
   * Ignores items-only updates while overlay keeps same open state (no jump mid-view).
   */
  useEffect(() => {
    const prevOpen = prevCarouselOpenRef.current;
    const openStateChanged = prevOpen !== keyMomentsCarouselOpen;
    prevCarouselOpenRef.current = keyMomentsCarouselOpen;

    if (!openStateChanged || items.length === 0 || !enabled) return;
    setCenterIndex(items.length - 1);
  }, [enabled, items.length, keyMomentsCarouselOpen]);

  useEffect(() => {
    if (items.length === 0) {
      hasInitializedRef.current = false;
      setCenterIndex(0);
      return;
    }
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setCenterIndex(items.length - 1);
      return;
    }
    setCenterIndex((prev) => Math.min(prev, items.length - 1));
  }, [items.length]);

  const handleSeek = useCallback((momentTimestampSeconds) => {
    if (!streamStartTimestamp || typeof handleLiveSeek !== 'function') return;
    const seekPosition = momentTimestampSeconds - streamStartTimestamp;
    handleLiveSeek(Math.max(seekable?.start ?? 0, seekPosition));
  }, [streamStartTimestamp, handleLiveSeek, seekable]);

  const handleCenterChange = useCallback((index) => {
    setCenterIndex(index);
  }, []);

  const handleSelect = useCallback((item) => {
    if (!item) return;
    setCenterIndex(items.findIndex((i) => i.incidenceId === item.incidenceId));
    handleSeek(item.timestamp);
  }, [items, handleSeek]);

  const activeItem = items[centerIndex];
  const activeId = activeItem?.incidenceId;

  return {
    enabled,
    gaps,
    items,
    unavailableIds,
    streamStartTimestamp,
    selectedId: activeId,
    onCenterChange: handleCenterChange,
    onSelect: handleSelect,
  };
}
